import { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo } from 'react';
import { commonBorderString, SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    createGridDropDownCommitAfter,
    GridCellConfig,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { defaultHRM2010_R01HeaderDto, HRM2010_R01HeaderDto } from './Dto_HRM2010_R01';
import { useHRM2010_R01CellChange } from './hooks/HRM2010_R01CellChange';
import { useHRM2010_R01Store } from './Store_HRM2010_R01';
import {
    buildHRM2010_R01KpiList,
    formatHRM2010_R01Hours,
    formatHRM2010_R01Time,
    isHRM2010_R01UnsavedRow,
} from './Util_HRM2010_R01';
import { KpiSection_HRM2010_R01 } from './KpiSection_HRM2010_R01';

/** 위 그리드의 참조와 행 선택 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2010_R01HeaderDto> | null>;
    onSelect: (row: HRM2010_R01HeaderDto) => void;
    setActiveGridType: (value: 'header' | 'detail') => void;
}

/** 편집할 수 있는 위 그리드 칸과 저장할 열 배치 키이다. */
const EDITABLE_COLUMNS = ['shiftCd', 'attendCd', 'startTime', 'endTime', 'remark'];
const COLUMN_STATE_KEY = 'HRM2010_R01-grid-column-state';
/** 저장된 열 배치를 복원할 때 쓰는 제목이다. */
const HEADER_NAMES = {
    rowIndex: '',
    empNo: '사원번호',
    empNm: '사원명',
    orgNm: '부서',
    shiftCd: '근무조',
    attendCd: '근태',
    startTime: '출근',
    endTime: '퇴근',
    lateMin: '지각(분)',
    earlyMin: '조퇴(분)',
    outHours: '외출(h)',
    pubOutHours: '공용외출(h)',
    breakHours: '휴게(h)',
    workHours: '정상(h)',
    overHours: '연장(h)',
    nightHours: '야간(h)',
    holidayHours: '휴일(h)',
    remark: '비고',
};

/** 사원별 근태 편집 그리드와 서버 값에서 만든 KPI를 표시한다. */
export const Table_HRM2010_R01 = ({ gridRef, onSelect, setActiveGridType }: Props) => {
    const state = useHRM2010_R01Store();
    /** 직접 입력 셀 변경과 드롭다운 선택을 각각 처리하는 함수를 가져온다. */
    const { handleHeaderCellChange, handleHeaderDropDownChange } = useHRM2010_R01CellChange();
    /** 드롭다운에서 Enter·Tab으로 선택하면 다음 편집 칸을 연다. 편집 컬럼이 상수라 처음 한 번만 만든다. */
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM2010_R01HeaderDto>(EDITABLE_COLUMNS),
        [],
    );
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM2010_R01HeaderDto>({
        gridRef,
        key: 'valueIndex',
        gridData: state.HRM2010_R01List,
        defaultGridData: defaultHRM2010_R01HeaderDto,
        selectedRow: state.selectedHRM2010_R01,
        originalGridData: state.originalHRM2010_R01List,
        updateRow: state.updateHRM2010_R01List,
    });
    /** 삭제 표시한 일일근태 행은 편집하지 않는다. */
    const editable = useCallback(
        (row?: HRM2010_R01HeaderDto) => !!row
            && !state.deletedHRM2010_R01List.includes(String(row.valueIndex)),
        [state.deletedHRM2010_R01List],
    );
    /** 행 번호와 일일근태 컬럼의 제목·너비·표시 형식을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM2010_R01HeaderDto>[]>(() => [
        /** 삭제·수정 상태만 행 번호에 표시하며 개별 행 등록 버튼은 연결하지 않는다. */
        ...getDefaultRowIndexColumnConfig((params: ICellRendererParams<HRM2010_R01HeaderDto>) => renderIndexCell(
            params,
            innerParams => ({
                ...getRowStyleFlags(innerParams, {
                    deletedList: state.deletedHRM2010_R01List,
                    selectedList: state.selectedHRM2010_R01
                        ? [String(state.selectedHRM2010_R01.valueIndex)] : [],
                    dataList: state.HRM2010_R01List,
                }),
                isDeleted: state.deletedHRM2010_R01List.includes(String(innerParams.data?.valueIndex)),
            }),
            () => {},
            () => {},
        )),
        {
            headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 150,
        },
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 84 },
        { headerName: '부서', colId: 'orgNm', field: 'orgNm', width: 96 },
        {
            headerName: '근무조', colId: 'shiftCd', field: 'shiftCd', width: 112,
            // 공통 드롭다운 옵션을 펼친다. 근무조 목록은 공통코드 모양(minorCd·codeNm)으로 바꿔 넘긴다.
            // 선택 시 값 반영과 함께 서버 재계산이 필요하므로 change 대신 화면 Hook의 함수를 연결한다.
            ...CommonCodeColumn.options<HRM2010_R01HeaderDto>(
                state.workShiftList.map(row => ({ minorCd: row.shiftCd, codeNm: row.shiftNm })),
                params => editable(params.data),
                handleHeaderDropDownChange,
                handleGridDropDownCommitAfter,
            ),
        },
        {
            headerName: '근태', colId: 'attendCd', field: 'attendCd', width: 104,
            // 출근·휴가·결근·출장 계열(G1~G4) 근태코드만 공통코드 모양으로 바꿔 넘긴다.
            ...CommonCodeColumn.options<HRM2010_R01HeaderDto>(
                state.attendCodeList
                    .filter(row => ['G1', 'G2', 'G3', 'G4'].includes(row.attendGroup))
                    .map(row => ({ minorCd: row.attendCd, codeNm: row.attendNm })),
                params => editable(params.data),
                handleHeaderDropDownChange,
                handleGridDropDownCommitAfter,
            ),
        },
        ...(['startTime', 'endTime'] as const).map(field => ({
            headerName: field === 'startTime' ? '출근' : '퇴근',
            colId: field,
            field,
            width: 96,
            editable: (params: { data?: HRM2010_R01HeaderDto }) => editable(params.data),
            cellEditor: 'agTextCellEditor',
            // 0930처럼 숫자 4자리로 입력해도 저장 전에 09:30으로 바꾼다.
            valueParser: (params: { newValue: unknown }) => formatHRM2010_R01Time(params.newValue),
            valueFormatter: (params: { value?: string; node?: { rowPinned?: string | null } | null }) =>
                params.node?.rowPinned ? '' : params.value || 'HH:MM',
        })),
        {
            headerName: '지각(분)', colId: 'lateMin', field: 'lateMin', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 0),
        },
        {
            headerName: '조퇴(분)', colId: 'earlyMin', field: 'earlyMin', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 0),
        },
        {
            headerName: '외출(h)', colId: 'outHours', field: 'outHours', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 2),
        },
        {
            headerName: '공용외출(h)', colId: 'pubOutHours', field: 'pubOutHours', width: 88,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 2),
        },
        {
            headerName: '휴게(h)', colId: 'breakHours', field: 'breakHours', width: 68,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 2),
        },
        {
            headerName: '정상(h)', colId: 'workHours', field: 'workHours', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 2),
        },
        {
            headerName: '연장(h)', colId: 'overHours', field: 'overHours', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 1),
        },
        {
            headerName: '야간(h)', colId: 'nightHours', field: 'nightHours', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 1),
        },
        {
            headerName: '휴일(h)', colId: 'holidayHours', field: 'holidayHours', width: 70,
            valueFormatter: params => params.node?.rowPinned || isHRM2010_R01UnsavedRow(params.data)
                ? '' : formatHRM2010_R01Hours(Number(params.value || 0), 1),
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 160,
            editable: params => editable(params.data),
        },
    ], [renderIndexCell, editable, state.deletedHRM2010_R01List, state.selectedHRM2010_R01,
        state.HRM2010_R01List, state.workShiftList, state.attendCodeList,
        handleHeaderDropDownChange, handleGridDropDownCommitAfter]);
    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
        [baseColumnDefs],
    );
    const columnDefs = useMemo(() => {
        if (state.columnState === null) return baseColumnDefs;

        const restored = GridCustomColumnDefs<HRM2010_R01HeaderDto>({
            baseColumnDefs,
            columnState: state.columnState,
            headerNames: HEADER_NAMES,
        });
        return restored.some(column => 'colId' in column && column.colId === 'rowIndex')
            ? restored : [baseColumnDefs[0], ...restored];
    }, [baseColumnDefs, state.columnState]);
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [],
        insertedDisabledCells: [],
        commonDisabledCells: [
            'empNm', 'orgNm', 'lateMin', 'earlyMin', 'outHours',
            'pubOutHours', 'breakHours', 'workHours', 'overHours',
            'nightHours', 'holidayHours',
        ],
        pointerCells: ['shiftCd', 'attendCd', 'startTime', 'endTime', 'remark'],
        insertedPointerCells: [],
        commonPointerCells: [],
        alignLeftCells: ['remark'],
    }), []);
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    const selectedIds = useMemo(
        () => state.selectedHRM2010_R01 ? [state.selectedHRM2010_R01.valueIndex] : [],
        [state.selectedHRM2010_R01],
    );
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM2010_R01HeaderDto>({
            idKey: 'valueIndex',
            selectedList: selectedIds,
            deletedList: state.deletedHRM2010_R01List.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedIds, state.deletedHRM2010_R01List, cellCssColumns]);
    const cellEventHandlers = useDetailTableEvents({
        detailList: state.HRM2010_R01List,
        editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });
    const cellClickActions = useMemo<CustomCellClickAction<HRM2010_R01HeaderDto>[]>(() => [{
        validate: (field, row) => EDITABLE_COLUMNS.includes(field) && editable(row),
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [editable]);
    const handleCellClick = CreateCellClickHandler(state.HRM2010_R01List, 'valueIndex', cellClickActions);
    const pinnedBottomRow = useMemo(() => {
        const active = state.HRM2010_R01List.filter(row =>
            !state.deletedHRM2010_R01List.includes(String(row.valueIndex)) && !row.isAttendMissing);
        // 하단 고정 행에는 삭제 표시와 저장되지 않은 미입력 행을 제외한 저장 건수만 표시한다.
        return [{ empNo: `${active.length}건` }];
    }, [state.HRM2010_R01List, state.deletedHRM2010_R01List]);
    const kpiList = useMemo(
        () => buildHRM2010_R01KpiList(state.HRM2010_R01List, state.deletedHRM2010_R01List),
        [state.HRM2010_R01List, state.deletedHRM2010_R01List],
    );
    const handleRowClick = useCallback((event: RowClickedEvent<HRM2010_R01HeaderDto>) => {
        if (event.node.rowPinned || !event.data) return;
        setActiveGridType('header');
        onSelect(event.data);
    }, [onSelect, setActiveGridType]);

    return (
        <section className="flex flex-col h-full min-h-0">
            {/* 공통 제목 오른쪽에 기존 시간 자동 계산 기준을 안내한다. */}
            <SmallTitleWithSubSpan title="일일근태 내역"
                subTitle="출·퇴근 시각과 외출 기록으로 정상/연장/야간/휴일 시간이 자동 계산됩니다" />
            {/* 제목 아래 KPI 칩 줄이다. 제목 박스와 그리드 사이 좌우 테두리가 끊기지 않도록 공통 테두리를 이어 그린다. */}
            <div className="px-[8px] py-1" style={{ borderLeft: commonBorderString, borderRight: commonBorderString }}>
                {/* 현재 목록에서 파생한 KPI 칩이다. */}
                <KpiSection_HRM2010_R01 kpiList={kpiList} />
            </div>
            {/* 공통 그리드 문맥과 편집 그리드이다. */}
            <GridContextWrapper<HRM2010_R01HeaderDto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                initSelected={() => state.setSelectedHRM2010_R01(undefined)}
                allSelected={() => {}}
                defaultRowData={defaultHRM2010_R01HeaderDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={state.setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
            >
                <Grid_Excel<HRM2010_R01HeaderDto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM2010_R01HeaderDto>({
                        idKey: 'valueIndex',
                        selectedList: selectedIds,
                        deletedList: state.deletedHRM2010_R01List.map(Number),
                    })}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                    onCellValueChanged={handleHeaderCellChange}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={state.setColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={state.scrollTop}
                    setScrollTop={state.setScrollTop}
                />
            </GridContextWrapper>
        </section>
    );
};
